import { IAlertLock } from "../../../modules/alerts/alert-group";
import { EmbeddingSearch, IAlertFilter, createInitialFilter } from "../../../modules/alerts/filter";
import { ILoggedInUser } from "../../models";
import { IAlert } from "../../models/alert.model";
import { createDefaultILoadableArray, createDefaultLoadable, ILoadableArray, Loadable } from "../../models/loadable";
import { getDefaultPauseAfterActionValue } from "../../scripts/client-flags/focus-mode";


export const KEYBOARD_MODE_KEY = "keyboard_mode";
export const WINDOW_SETTINGS_KEY = "window_settings";
export const ALERT_VIDEO_AUTOPLAY_KEY = "alert_video_autoplay";
export const ALERT_VIDEO_OVERLAYS_KEY = "alert_video_overlays";
export const DISMISS_ON_MUTE_KEY = "dismiss_on_mute";
export const PAUSE_AFTER_ALERT_ACTION = "pause_after_alert_action";
export const PAUSE_AFTER_ALERT_ACTION_APPLIED = "pause_after_alert_action_applied";


export function loadPauseAfterAlertAction(user: ILoggedInUser): boolean {
  const defaultValue: string = getDefaultPauseAfterActionValue(user)
  return JSON.parse(localStorage.getItem(`${user.id.slice(0, 6)}_${PAUSE_AFTER_ALERT_ACTION}`) ?? defaultValue);
}

export function storePauseAfterAlertAction(user: ILoggedInUser, state: boolean) {
  localStorage.setItem(`${user.id.slice(0, 6)}_${PAUSE_AFTER_ALERT_ACTION}`, JSON.stringify(state))
}

export type IAlerts = ILoadableArray<IAlert> & {
  total: number
  loadingPage: boolean
}

export interface IAlertPagination extends Loadable {
  current: number
}

export interface ISelectedAlerts extends Loadable {
  selected: Set<string>,
  deselected: Set<string>,
  allSelected: boolean,
  currentCount: number
}

export interface IAlertWindow {
  alerts: string[]
  fullAlerts: IAlert[]
  currentAlert: number // The windows internal alert index
  currentAlertId: string | undefined
  size: number // Global size of window
  interval: number, // Global "length" of window
  selected: string[] // contains a list of all the selected indexes
  windows: string[][]  // All potential groups
  alertsToBeDismissed: string[]
  currentWindowIndex: number
  previousWindowIndex: number
  canAcquireLocks: boolean
  alertIdsToRelease: string[]
  requestingNextLock: boolean
  // Window alert ids whose group thumbnail has loaded or given up, so the next clips can preload.
  settledThumbnails: string[]
}

export interface IAlertState {
  filter: IAlertFilter;
  alerts: IAlerts;
  page: IAlertPagination;
  window: IAlertWindow;
  keyboardMode: boolean;
  alertVideoAutoplay: boolean;
  alertVideoOverlays: boolean;
  dismissOnMute: boolean;
  pauseAfterAlertAction: boolean;
  locks: Record<string, IAlertLock>;
  selectedAlerts: ISelectedAlerts;
  socketConnected: boolean;
  isAlertsPageActive: boolean;
  paused: {
    paused: boolean
  };
  userId: string;
  viewTimestamps: Record<string, string>
  hiddenAlertIds: string[];
  alertsLoadedAt: number;
  stagedEmbeddingSearches: EmbeddingSearch[];
  identityDetailsLoading: Record<string, boolean>;
}

// The socket ignores alert mutations while a similarity filter is active, so the
// dashboard polls at this interval instead (see pollSimilarityAlerts$).
export const SIMILARITY_POLL_MS = 20000;

function loadWindow(): IAlertWindow {
  const settings = JSON.parse(localStorage.getItem(WINDOW_SETTINGS_KEY) ?? "null") ?? {
    size: 6,
    interval: 90
  };
  return {
    alerts: [],
    fullAlerts: [],
    currentAlert: 0,
    currentAlertId: undefined,
    selected: [],
    windows: [],
    alertsToBeDismissed: [],
    currentWindowIndex: 0,
    previousWindowIndex: 0,
    canAcquireLocks: false,
    alertIdsToRelease: [],
    requestingNextLock: false,
    settledThumbnails: [],
    ...settings
  }
}

export const initialAlertSelectState: ISelectedAlerts = {
  ...createDefaultLoadable(),
  selected: new Set(),
  deselected: new Set(),
  allSelected: false,
  currentCount: 0
}

export const initialAlertState: IAlertState = {
  filter: createInitialFilter(),
  alerts: {
    ...createDefaultILoadableArray(),
    total: 0,
    loadingPage: false
  },
  page: {
    ...createDefaultLoadable(),
    current: 1
  },
  window: loadWindow(),
  // TODO: QQ, should we move settings to config state?
  keyboardMode: JSON.parse(localStorage.getItem(KEYBOARD_MODE_KEY) ?? "true"),
  alertVideoAutoplay: JSON.parse(localStorage.getItem(ALERT_VIDEO_AUTOPLAY_KEY) ?? "true"),
  alertVideoOverlays: JSON.parse(localStorage.getItem(ALERT_VIDEO_OVERLAYS_KEY) ?? "false"),
  dismissOnMute: JSON.parse(localStorage.getItem(DISMISS_ON_MUTE_KEY) ?? "true"),
  pauseAfterAlertAction: false,
  selectedAlerts: {
    ...initialAlertSelectState
  },
  paused: {
    paused: false
  },
  locks: {},
  socketConnected: false,
  isAlertsPageActive: false,
  hiddenAlertIds: [],
  userId: '',
  viewTimestamps: {},
  alertsLoadedAt: 0,
  stagedEmbeddingSearches: [],
  identityDetailsLoading: {},
}
